import { UiButtonDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { EzFormComponent } from '@template-ui/components/ez-form/ez-form.component';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { ButtonConfig, InputSelectOption } from '@template-ui/types';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';

import {
  WorkflowDefinitionDocument,
  WorkflowDefinitionEdge,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';
import { WorkflowDraftConfirmationService } from './workflow-draft-confirmation.service';
import { replaceWorkflowPath } from './workflow-path-authoring';
import type { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

interface WorkflowEdgeFormValues {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  outcomeCode?: string | null;
  pathKindCode?: string | null;
}

interface WorkflowEdgeRow {
  edge: WorkflowDefinitionEdge | null;
  index: number;
  fromLabel: string;
  toLabel: string;
  relationshipLabel: string;
  resultLabel: string | null;
}

@Component({
  selector: 'phms-platform-workflow-draft-edge-editor',
  imports: [UiButtonDirective, CommonModule, UiLibraryModule],
  templateUrl: './platform-workflow-draft-edge-editor.component.html',
  styleUrl: './platform-workflow-draft-edge-editor.component.scss',
})
export class PlatformWorkflowDraftEdgeEditorComponent implements OnChanges {
  @Input() contextual = false;
  @Input() inspectionMode = false;
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() canAuthor = false;
  @Input() interactionBlocked = false;
  @Input() resetContextEdgeIndex: number | null | undefined;
  @Input() resetVersion = 0;

  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();

  @ViewChild('edgeForm')
  private edgeForm?: EzFormComponent;

  @ViewChild('edgeEditorHeading')
  private edgeEditorHeading?: ElementRef<HTMLElement>;

  @ViewChild('edgeSectionHeading')
  private edgeSectionHeading?: ElementRef<HTMLElement>;

  @ViewChild('edgeErrorSummary')
  private edgeErrorSummary?: ElementRef<HTMLElement>;

  readonly nodeOptions = signal<InputSelectOption[]>([]);
  readonly sourceOptions = signal<InputSelectOption[]>([]);
  readonly destinationOptions = signal<InputSelectOption[]>([]);
  readonly selectedEdgeId = signal<string | null>(null);
  readonly selectedEdgeIndex = signal<number | null>(null);
  readonly isAddingEdge = signal(false);
  readonly pendingChanges = signal(false);
  readonly statusMessage = signal<string | null>(null);
  readonly errorMessage = signal<string | null>(null);

  edgeFormValues: WorkflowEdgeFormValues = this.emptyFormValues();
  edgeFormConfig: InputConfig[] = this.buildFormConfig(false);

  private initialized = false;
  private selectedEdgeBaseline: string | null = null;
  private selectedEdgeSource: WorkflowDefinitionEdge | null = null;
  private pendingContext: string | null = null;
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private localConfirmationInProgress = false;

  constructor(
    private readonly confirmation: WorkflowDraftConfirmationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['document']) {
      this.updateNodeOptions();
    }

    if (!this.document) {
      this.clearEditor();
      this.initialized = true;
      return;
    }

    const documentChange = changes['document'];
    const resetChange = changes['resetVersion'];
    const startsDocumentSession =
      !this.initialized ||
      (!!documentChange &&
        (documentChange.previousValue === null ||
          documentChange.previousValue === undefined));
    const resetRequested =
      !!resetChange &&
      !resetChange.firstChange &&
      resetChange.previousValue !== resetChange.currentValue;

    if (startsDocumentSession || resetRequested) {
      this.resetFromDocument();
      this.initialized = true;
      return;
    }

    if (documentChange && !this.isAddingEdge()) {
      const selectedId = this.selectedEdgeId();
      const selectedIndex = this.selectedEdgeIndex();
      if (!selectedId && selectedIndex !== null) {
        if (this.edgeEntries[selectedIndex] !== null) {
          this.resetFromDocument();
        }
        return;
      }
      if (!selectedId) {
        return;
      }

      const nextIndex = this.edgeEntries.findIndex(
        (edge) => edge?.id === selectedId,
      );
      if (nextIndex === -1) {
        if (this.pendingChanges()) {
          this.selectedEdgeIndex.set(null);
          this.showError('This saved path is no longer available. Your input is kept. Reset changes to review the current paths.');
        } else this.resetFromDocument();
      } else if (!this.pendingChanges()) {
        this.loadEdge(this.edgeEntries[nextIndex]!, nextIndex);
      } else {
        this.selectedEdgeIndex.set(nextIndex);
      }
    }
  }

  get edges(): Array<WorkflowDefinitionEdge | null> {
    return this.edgeEntries;
  }

  get edgeRows(): WorkflowEdgeRow[] {
    return this.edgeEntries.map((edge, index) => {
      if (!edge) {
        return {
          edge,
          index,
          fromLabel: 'Invalid path entry',
          toLabel: 'No destination available',
          relationshipLabel: `Invalid path entry ${index + 1}`,
          resultLabel: null,
        };
      }

      const fromLabel = this.nodeLabel(edge.fromNodeId);
      const toLabel = this.nodeLabel(edge.toNodeId);
      return {
        edge,
        index,
        fromLabel,
        toLabel,
        relationshipLabel: `${edge.outcomeCode ? edge.outcomeCode + ': ' : ''}${fromLabel} to ${toLabel}${edge.pathKindCode === 'return' ? ' (return)' : ''}`,
        resultLabel: this.pathResultLabel(edge),
      };
    });
  }

  get selectedEdge(): WorkflowDefinitionEdge | null {
    const index = this.selectedEdgeIndex();
    if (index === null) {
      return null;
    }

    const edge = this.edgeEntries[index] ?? null;
    return this.pendingChanges() && edge?.id !== this.selectedEdgeId() ? null : edge;
  }

  get selectedEdgeRow(): WorkflowEdgeRow | null {
    const index = this.selectedEdgeIndex();
    if (this.pendingChanges() && !this.isAddingEdge() && !this.selectedEdge && this.selectedEdgeSource) {
      const edge = this.selectedEdgeSource;
      const fromLabel = this.nodeLabel(edge.fromNodeId), toLabel = this.nodeLabel(edge.toNodeId);
      return { edge, index: -1, fromLabel, toLabel, relationshipLabel: `${fromLabel} to ${toLabel}`, resultLabel: this.pathResultLabel(edge) };
    }
    if (index === null || index < 0 || index >= this.edgeEntries.length) {
      return null;
    }

    return this.edgeRows[index];
  }

  get hasMalformedSelection(): boolean {
    return this.selectedEdgeRow?.edge === null;
  }

  private pathResultLabel(edge: WorkflowDefinitionEdge): string | null {
    const labels: Record<string, string> = {
      approved: 'Approved',
      changes_required: 'Changes required',
    };
    const declared = this.declaredResults(edge.fromNodeId).find(
      (result) => result.code === edge.outcomeCode,
    );
    const result = edge.outcomeCode
      ? declared?.label || labels[edge.outcomeCode] || edge.outcomeCode
      : null;
    return (
      [result, edge.pathKindCode === 'return' ? 'Return for correction' : null]
        .filter(Boolean)
        .join(' · ') || null
    );
  }

  get isRoutingSource(): boolean {
    return (
      this.document?.nodes.find(
        (node) => node?.id === this.edgeFormValues.fromNodeId,
      )?.typeCode === 'routing_decision'
    );
  }

  get hasUnsupportedRoutingSettings(): boolean {
    return (
      !this.isRoutingSource &&
      !!(this.edgeFormValues.outcomeCode || this.edgeFormValues.pathKindCode)
    );
  }

  get pathGuidance(): string {
    if (this.hasUnsupportedRoutingSettings)
      return 'This saved path has routing settings on a regular work stage. Choose a condition as its source, or use an ordinary continuation to repair it.';
    const source = this.document?.nodes.find(
      (node) => node?.id === this.edgeFormValues.fromNodeId,
    );
    if (
      source?.declaration?.behavior?.commands?.some(
        (command) =>
          command?.capability?.key?.code === 'workflow.preparation.submit',
      )
    )
      return 'This action submits work directly to a compatible review step. Keep that handoff together; add later work after the review outcome.';
    const next = this.document?.nodes.find(
      (node) => node?.id === this.edgeFormValues.toNodeId,
    );
    if (next?.typeCode === 'routing_decision')
      return 'This condition evaluates the step that leads into it. Keep it attached to that step; add later work on a condition result path.';
    return this.isRoutingSource
      ? 'Choose the result this path handles. Continue moves forward; a return goes back to an earlier step already visited by this process.'
      : 'Work continues to the selected destination when this step is completed. For branches, configure action outcomes in Step behavior → Outcomes & routing.';
  }

  useOrdinaryContinuation(): void {
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      !this.hasUnsupportedRoutingSettings
    )
      return;
    this.onEdgeFormChange({
      ...this.edgeFormValues,
      outcomeCode: null,
      pathKindCode: null,
    });
  }

  get addButtonConfig(): ButtonConfig[] {
    return [
      {
        id: 'addEdge',
        buttonText: 'Add path',
        disabled: !this.canAuthor || this.interactionBlocked,
      },
    ];
  }

  get editorButtonConfig(): ButtonConfig[] {
    return [
      {
        id: 'applyEdge',
        buttonText: this.isAddingEdge() ? 'Add path' : 'Apply path changes',
        disabled:
          !this.canAuthor || !this.pendingChanges() || this.interactionBlocked,
      },
      {
        id: 'resetEdge',
        buttonText: 'Reset path',
        severity: 'secondary',
        disabled:
          !this.canAuthor ||
          (!this.pendingChanges() && !this.isAddingEdge()) ||
          this.interactionBlocked,
      },
      {
        id: 'removeEdge',
        buttonText: 'Remove path',
        severity: 'secondary',
        disabled:
          !this.canAuthor ||
          this.isAddingEdge() ||
          this.selectedEdgeIndex() === null ||
          this.interactionBlocked,
      },
    ];
  }

  onAddButtonClick(button: ButtonConfig): void {
    if (button.id === 'addEdge') {
      this.beginAddEdge();
    }
  }

  onEditorButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case 'applyEdge':
        this.applyEdgeChanges();
        break;
      case 'resetEdge':
        this.resetEdgeForm();
        break;
      case 'removeEdge':
        this.requestRemoveEdge();
        break;
      default:
        break;
    }
  }

  beginAddEdge(): void {
    if (!this.canAuthor || this.interactionBlocked || !this.document) {
      return;
    }

    this.requestDiscardPendingChanges(() => {
      this.startAddEdge(
        this.emptyFormValues(),
        'Choose where this path starts and where work continues.',
      );
    });
  }

  requestDeactivate(accepted: () => void, cancelled: () => void): void {
    this.requestDiscardPendingChanges(accepted, cancelled);
  }

  discardPendingChanges(): void {
    const selectedIndex = this.selectedEdgeIndex();
    const selectedId = this.selectedEdgeId();
    const selectedEntry =
      selectedIndex === null ? undefined : this.edgeEntries[selectedIndex];

    if (
      selectedIndex !== null &&
      selectedEntry &&
      selectedEntry.id === selectedId
    ) {
      this.loadEdge(selectedEntry, selectedIndex);
      return;
    }

    if (
      selectedIndex !== null &&
      selectedIndex >= 0 &&
      selectedIndex < this.edgeEntries.length &&
      selectedEntry === null
    ) {
      this.loadMalformedEdge(selectedIndex);
      return;
    }

    if (selectedId) {
      const currentIndex = this.edgeEntries.findIndex(
        (edge) => edge?.id === selectedId,
      );
      const currentEdge = this.edgeEntries[currentIndex];
      if (currentIndex !== -1 && currentEdge) {
        this.loadEdge(currentEdge, currentIndex);
        return;
      }
    }

    this.resetFromDocument();
  }

  focusPendingChanges(): void {
    this.focusAfterRender(
      () => this.edgeEditorHeading ?? this.edgeSectionHeading,
    );
  }

  reviewEdge(
    edge: WorkflowDefinitionEdge,
    index: number,
    moveFocusToInspector = true,
  ): void {
    if (this.interactionBlocked) {
      return;
    }

    this.requestDiscardPendingChanges(() =>
      this.loadEdgeForReview(edge, index, moveFocusToInspector),
    );
  }

  reviewEdgeAt(index: number, moveFocusToInspector = true): void {
    if (index < 0 || index >= this.edgeEntries.length) {
      return;
    }

    const edge = this.edgeEntries[index];
    if (edge) {
      if (this.interactionBlocked) {
        this.requestDiscardPendingChanges(() =>
          this.loadEdgeForReview(edge, index, moveFocusToInspector),
        );
        return;
      }
      this.reviewEdge(edge, index, moveFocusToInspector);
      return;
    }

    this.requestDiscardPendingChanges(() => {
      this.loadMalformedEdge(index);
      this.statusMessage.set(`Reviewing invalid path entry ${index + 1}.`);
      if (moveFocusToInspector) {
        this.focusAfterRender(() => this.edgeEditorHeading);
      }
    });
  }

  /** Loads one rechecked clean path from the owner's latest document without discarding input. */
  reviewExactEdge(
    document: WorkflowDefinitionDocument,
    edgeId: string,
    index: number,
    moveFocusToInspector = true,
  ): boolean {
    if (
      this.pendingChanges() || this.isAddingEdge() ||
      !Number.isInteger(index) || index < 0 ||
      typeof edgeId !== 'string' || !edgeId.trim() ||
      !Array.isArray(document.edges) || index >= document.edges.length
    ) return false;
    const edge = document.edges[index];
    if (
      !edge || typeof edge !== 'object' || Array.isArray(edge) ||
      edge.id !== edgeId ||
      typeof edge.fromNodeId !== 'string' || typeof edge.toNodeId !== 'string' ||
      (edge.outcomeCode != null && typeof edge.outcomeCode !== 'string') ||
      (edge.pathKindCode != null && typeof edge.pathKindCode !== 'string') ||
      document.edges.filter(candidate => candidate?.id === edgeId).length !== 1
    ) return false;

    this.document = document;
    this.initialized = true;
    this.loadEdgeForReview(edge, index, moveFocusToInspector);
    return true;
  }

  private loadEdgeForReview(
    edge: WorkflowDefinitionEdge,
    index: number,
    moveFocusToInspector: boolean,
  ): void {
    this.loadEdge(edge, index);
    this.statusMessage.set(
      `Reviewing path from ${this.nodeLabel(edge.fromNodeId)} to ${this.nodeLabel(edge.toNodeId)}.`,
    );
    if (moveFocusToInspector) {
      this.focusAfterRender(() => this.edgeEditorHeading);
    }
  }

  focusEdge(edgeId: string): void {
    if (this.interactionBlocked || !this.document) {
      return;
    }

    const index = this.edgeEntries.findIndex((edge) => edge?.id === edgeId);
    if (index === -1) {
      this.showError(`Edge ${edgeId} is no longer available in this draft.`);
      return;
    }

    const edge = this.edgeEntries[index];
    if (edge) {
      this.reviewEdge(edge, index);
    }
  }

  focusEdgePath(source: 'validation' | 'review' = 'validation'): void {
    this.statusMessage.set(
      source === 'review'
        ? 'Showing the saved path changes for this draft.'
        : 'Validation identified an issue with the routed edge graph.',
    );
    this.focusAfterRender(() => this.edgeSectionHeading);
  }

  focusNodePath(nodeId: string, endpoint: 'source' | 'target'): void {
    if (this.interactionBlocked || !this.document) {
      return;
    }

    const node = this.document.nodes.find(
      (candidate) => candidate.id === nodeId,
    );
    if (!node) {
      this.showError(
        `Workflow step ${nodeId} is no longer available in this draft.`,
      );
      return;
    }

    const stepLabel = node.displayName || node.stepCode || node.id;
    if (!this.canAuthor) {
      this.statusMessage.set(
        `${stepLabel} has no connected paths. Path authoring is not available right now.`,
      );
      this.focusAfterRender(() => this.edgeSectionHeading);
      return;
    }

    this.requestDiscardPendingChanges(() => {
      this.startAddEdge(
        {
          ...this.emptyFormValues(),
          fromNodeId: endpoint === 'source' ? nodeId : '',
          toNodeId: endpoint === 'target' ? nodeId : '',
        },
        endpoint === 'source'
          ? `Add a path from ${stepLabel}. The source step is already selected.`
          : `Add a path to ${stepLabel}. The target step is already selected.`,
      );
    });
  }

  onEdgeFormChange(values: unknown): void {
    if (!this.canAuthor || this.interactionBlocked) {
      return;
    }

    const nextValues = values as Partial<WorkflowEdgeFormValues>;
    const sourceChanged =
      nextValues.fromNodeId !== undefined &&
      nextValues.fromNodeId !== this.edgeFormValues.fromNodeId;
    this.edgeFormValues = {
      id: this.isAddingEdge()
        ? ''
        : (this.selectedEdge?.id ?? this.edgeFormValues.id),
      fromNodeId: nextValues.fromNodeId ?? this.edgeFormValues.fromNodeId,
      toNodeId: nextValues.toNodeId ?? this.edgeFormValues.toNodeId,
      outcomeCode:
        nextValues.outcomeCode !== undefined
          ? nextValues.outcomeCode
          : this.edgeFormValues.outcomeCode,
      pathKindCode:
        nextValues.pathKindCode !== undefined
          ? nextValues.pathKindCode
          : this.edgeFormValues.pathKindCode,
    };
    if (sourceChanged && !this.isRoutingSource) {
      this.edgeFormValues.outcomeCode = null;
      this.edgeFormValues.pathKindCode = null;
    }
    this.updateNodeOptions();
    if (sourceChanged)
      this.edgeFormConfig = this.buildFormConfig(this.isAddingEdge());
    this.errorMessage.set(null);
    this.statusMessage.set(null);
    this.setPendingChanges(true);
  }

  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.document): readonly WorkflowEditorPendingScope[] {
    return [{
      id: 'path-input', label: 'path input',
      state: () => this.pendingPathState(latestDocument()),
      fingerprint: () => JSON.stringify([this.pendingChanges(), this.isAddingEdge(), this.edgeFormValues, this.selectedEdgeId(), this.selectedEdgeBaseline,
        this.pendingContext, this.resetVersion, this.canAuthor, this.interactionBlocked, latestDocument()]),
      apply: () => { const document = latestDocument(); return !!document && this.applyPendingChanges(document); },
      discard: () => { const document = latestDocument(); if (document) this.document = document; this.discardPendingChanges(); },
      focus: () => this.focusPendingChanges(),
    }];
  }
  private pathContext(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document?.workflowCode, document?.definitionVersionCode, document?.packReference, this.resetVersion]);
  }
  private pendingPathState(document: WorkflowDefinitionDocument | null): WorkflowEditorBufferState {
    if (!this.pendingChanges()) return 'clean';
    if (!document || this.pathContext(document) !== this.pendingContext) return 'stale';
    const matches = document.edges.filter(edge => edge?.id === this.selectedEdgeId());
    if (!this.isAddingEdge() && (matches.length !== 1 || JSON.stringify(matches[0]) !== this.selectedEdgeBaseline)) return 'stale';
    if (!this.canAuthor || this.interactionBlocked || !this.edgeForm?.theFormGroup || this.edgeForm.theFormGroup.invalid) return 'invalid';
    const current = this.isAddingEdge() ? undefined : matches[0];
    const values = this.edgeFormValues;
    try {
      replaceWorkflowPath(document, { ...current, id: current?.id ?? '', fromNodeId: values.fromNodeId.trim(), toNodeId: values.toNodeId.trim(),
        ...(values.outcomeCode !== undefined ? { outcomeCode: values.outcomeCode?.trim() || null } : {}),
        ...(values.pathKindCode !== undefined ? { pathKindCode: values.pathKindCode?.trim() || null } : {}) }, current?.id);
      return 'valid';
    } catch { return 'invalid'; }
  }
  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (!this.pendingChanges()) return true;
    if (!this.canAuthor || this.interactionBlocked) return false;
    if (this.pendingPathState(latestDocument ?? this.document) === 'stale') {
      this.showError('This path changed while you were editing. Your edits have been kept. Use Reset changes to review its current settings before applying another change.');
      return false;
    }
    if (latestDocument) {
      const selectedId = this.selectedEdgeId();
      if (!this.isAddingEdge() && selectedId) {
        const matches = latestDocument.edges
          .map((edge, index) => ({ edge, index }))
          .filter(({ edge }) => edge?.id === selectedId);
        if (matches.length !== 1) {
          this.showError(
            'The selected path is no longer available. Your edits remain open.',
          );
          return false;
        }
        this.selectedEdgeIndex.set(matches[0].index);
      }
      this.document = latestDocument;
    }
    const capture = this.documentChange.subscribe(
      (value) => (this.document = value),
    );
    try {
      this.applyEdgeChanges();
      return !this.pendingChanges();
    } finally {
      capture.unsubscribe();
    }
  }

  applyEdgeChanges(): void {
    const document = this.document;
    if (!document || !this.canAuthor || this.interactionBlocked) {
      return;
    }
    if (this.pendingChanges() && this.pendingPathState(document) === 'stale') {
      this.showError('This path changed while you were editing. Your edits have been kept. Use Reset changes to review its current settings before applying another change.');
      return;
    }

    const selectedIndex = this.selectedEdgeIndex();
    const selectedEdge =
      selectedIndex === null ? null : (this.edgeEntries[selectedIndex] ?? null);
    if (
      !this.isAddingEdge() &&
      selectedEdge &&
      JSON.stringify(selectedEdge) !== this.selectedEdgeBaseline
    ) {
      this.showError(
        'This path changed while you were editing. Your edits have been kept. Use Reset changes to review its current settings before applying another change.',
      );
      return;
    }
    if (!this.ensureFormValid()) return;
    const nextEdge: WorkflowDefinitionEdge = {
      ...selectedEdge,
      id: this.isAddingEdge() ? '' : (selectedEdge?.id ?? ''),
      fromNodeId: this.edgeFormValues.fromNodeId.trim(),
      toNodeId: this.edgeFormValues.toNodeId.trim(),
      ...(this.edgeFormValues.outcomeCode !== undefined
        ? { outcomeCode: this.edgeFormValues.outcomeCode?.trim() || null }
        : {}),
      ...(this.edgeFormValues.pathKindCode !== undefined
        ? { pathKindCode: this.edgeFormValues.pathKindCode?.trim() || null }
        : {}),
    };
    if (!nextEdge.fromNodeId || !nextEdge.toNodeId) {
      this.showError(
        'Choose where the path starts and where work goes next. Both work stages are required.',
      );
      return;
    }

    const availableNodeIds = new Set(
      document.nodes.filter((node) => !!node).map((node) => node.id),
    );
    if (
      !availableNodeIds.has(nextEdge.fromNodeId) ||
      !availableNodeIds.has(nextEdge.toNodeId)
    ) {
      this.showError(
        'Select work stages that are still available in this draft.',
      );
      return;
    }

    if (!this.isAddingEdge() && (selectedIndex === null || !selectedEdge)) {
      this.showError(
        'The selected path is no longer available. Review the draft and try again.',
      );
      return;
    }
    let nextDocument: WorkflowDefinitionDocument;
    try {
      nextDocument = replaceWorkflowPath(
        document,
        nextEdge,
        this.isAddingEdge() ? undefined : selectedEdge!.id,
      );
    } catch (error) {
      this.showError(
        error instanceof Error
          ? error.message
          : 'This path could not be changed. Review its source and destination.',
      );
      return;
    }
    const nextIndex = this.isAddingEdge()
      ? nextDocument.edges.length - 1
      : selectedIndex!;
    const appliedEdge = nextDocument.edges[nextIndex];

    this.isAddingEdge.set(false);
    this.selectedEdgeId.set(appliedEdge.id);
    this.selectedEdgeIndex.set(nextIndex);
    this.edgeFormValues = { ...appliedEdge };
    this.selectedEdgeBaseline = JSON.stringify(appliedEdge);
    this.updateNodeOptions();
    this.edgeFormConfig = this.buildFormConfig(false);
    this.errorMessage.set(null);
    this.setPendingChanges(false);
    this.statusMessage.set(
      selectedIndex === null
        ? 'Path added to the working draft.'
        : 'Path changes applied to the working draft.',
    );
    this.documentChange.emit(nextDocument);
  }

  resetEdgeForm(): void {
    if (!this.canAuthor || this.interactionBlocked) {
      return;
    }

    if (this.isAddingEdge()) {
      this.resetFromDocument();
      this.statusMessage.set('New path cancelled.');
      this.focusAfterRender(
        () => this.edgeEditorHeading ?? this.edgeSectionHeading,
      );
      return;
    }

    const selectedEdge = this.selectedEdge;
    const selectedIndex = this.selectedEdgeIndex();
    if (selectedEdge && selectedIndex !== null) {
      this.loadEdge(selectedEdge, selectedIndex);
      this.statusMessage.set('Unapplied path changes reset.');
      this.focusAfterRender(() => this.edgeEditorHeading);
    } else if (this.pendingChanges()) {
      this.discardPendingChanges();
      this.statusMessage.set('Unapplied path changes reset.');
      this.focusAfterRender(() => this.edgeEditorHeading ?? this.edgeSectionHeading);
    }
  }

  requestRemoveEdge(): void {
    const selectedEdge = this.selectedEdge;
    const selectedRow = this.selectedEdgeRow;
    const selectedIndex = this.selectedEdgeIndex();
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      !selectedRow ||
      selectedIndex === null
    ) {
      return;
    }

    const heading = selectedEdge
      ? `Remove path from ${selectedRow.fromLabel} to ${selectedRow.toLabel}?`
      : 'Remove invalid path entry?';
    const message = selectedEdge
      ? `This removes the intended map connection from ${selectedRow.fromLabel} to ${selectedRow.toLabel} from the working draft.`
      : `Invalid path entry ${selectedIndex + 1} will be removed from the working draft.`;
    const selectedFingerprint = JSON.stringify(selectedEdge);
    const malformedEntriesFingerprint = selectedEdge
      ? null
      : JSON.stringify(this.document?.edges);
    const removalSession = this.resetVersion;
    void this.confirmLocally({
        heading,
        message,
        confirmText: 'Remove path',
        cancelText: 'Keep path',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (!confirmed) return;
        if (!this.canAuthor || this.interactionBlocked) {
          this.showError(
            'Path removal is no longer available. Review your current editing access before trying again.',
          );
          return;
        }
        if (!this.document || this.resetVersion !== removalSession) {
          this.showError(
            'The draft changed while you were confirming. Select the path again before removing it.',
          );
          return;
        }
        let removalIndex = selectedIndex;
        if (selectedEdge) {
          const matches = this.edgeEntries
            .map((edge, index) => ({ edge, index }))
            .filter(({ edge }) => edge?.id === selectedEdge.id);
          if (
            !selectedEdge.id?.trim() ||
            matches.length !== 1 ||
            JSON.stringify(matches[0].edge) !== selectedFingerprint
          ) {
            this.showError(
              'This path changed or is no longer uniquely available. Review it before removing it.',
            );
            return;
          }
          removalIndex = matches[0].index;
        } else if (
          this.edgeEntries[selectedIndex] !== null ||
          JSON.stringify(this.document.edges) !== malformedEntriesFingerprint
        ) {
          this.showError(
            'The invalid path evidence changed while you were confirming. Review it before removing it.',
          );
          return;
        }
        this.removeEdge(removalIndex);
      });
  }

  nodeLabel(nodeId: string): string {
    const node = this.document?.nodes.find(
      (candidate) => candidate.id === nodeId,
    );
    if (!node) {
      return 'Missing work stage';
    }

    return this.nodeDisplayName(node);
  }

  trackEdgeRow(_index: number, row: WorkflowEdgeRow): number {
    return row.index;
  }

  private removeEdge(selectedIndex: number): void {
    const document = this.document;
    if (
      !document ||
      !this.canAuthor ||
      this.interactionBlocked ||
      selectedIndex < 0 ||
      selectedIndex >= this.edgeEntries.length
    ) {
      this.showError(
        'The selected path is no longer available. Review the draft and try again.',
      );
      return;
    }

    const removedEdge = this.edgeEntries[selectedIndex];
    const removedLabel = removedEdge
      ? `${this.nodeLabel(removedEdge.fromNodeId)} to ${this.nodeLabel(removedEdge.toNodeId)}`
      : `invalid path entry ${selectedIndex + 1}`;
    const nextEdges = this.edgeEntries.filter(
      (_, index) => index !== selectedIndex,
    );
    this.setPendingChanges(false);
    this.errorMessage.set(null);
    this.isAddingEdge.set(false);

    if (nextEdges.length > 0) {
      const nextIndex = Math.min(selectedIndex, nextEdges.length - 1);
      const nextEdge = nextEdges[nextIndex];
      if (nextEdge) {
        this.loadEdge(nextEdge, nextIndex);
      } else {
        this.loadMalformedEdge(nextIndex);
      }
    } else {
      this.selectedEdgeId.set(null);
      this.selectedEdgeIndex.set(null);
      this.selectedEdgeBaseline = null;
      this.selectedEdgeSource = null;
      this.edgeFormValues = this.emptyFormValues();
      this.edgeFormConfig = this.buildFormConfig(false);
    }

    this.statusMessage.set(
      `Path ${removedLabel} removed from the working draft.`,
    );
    this.documentChange.emit({
      ...document,
      edges: nextEdges as WorkflowDefinitionEdge[],
    });
    this.focusAfterRender(
      () => this.edgeEditorHeading ?? this.edgeSectionHeading,
    );
  }

  private requestDiscardPendingChanges(
    action: () => void,
    cancelled: () => void = () => undefined,
  ): void {
    if (this.destroyRef.destroyed || this.localConfirmationInProgress || this.ownerViews.focusSuspended()) { cancelled(); return; }
    if (!this.pendingChanges()) {
      action();
      return;
    }

    const scopes = this.pendingEditorScopes();
    const context = () => JSON.stringify([this.document, this.resetVersion, this.selectedEdgeId(), this.selectedEdgeIndex(), this.isAddingEdge(), this.canAuthor, this.interactionBlocked,
      scopes.map(scope => scope.state() === 'clean' ? [scope.id, 'clean'] : [scope.id, scope.state(), scope.fingerprint()]),
      this.pendingEditorScopes().filter(scope => scope.state() !== 'clean').map(scope => [scope.id, scope.state(), scope.fingerprint()])]);
    const before = context();
    void this.confirmLocally({
        heading: 'Discard path changes?',
        message: 'Unapplied changes to the selected path will be lost.',
        confirmText: 'Discard changes',
        cancelText: 'Keep editing',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (confirmed) {
          if (context() !== before) {
            this.showError('The path input or context changed while confirmation was open. Review the current input before trying again.');
            cancelled();
            return;
          }
          this.discardPendingChanges();
          if (!this.pendingChanges()) action();
          else { this.focusPendingChanges(); cancelled(); }
        } else {
          this.focusPendingChanges();
          cancelled();
        }
      });
  }

  private resetFromDocument(): void {
    if (this.edgeEntries.length > 0) {
      const requestedIndex = this.resetContextEdgeIndex;
      const selectedIndex =
        requestedIndex !== undefined &&
        requestedIndex !== null &&
        requestedIndex >= 0 &&
        requestedIndex < this.edgeEntries.length
          ? requestedIndex
          : 0;
      const selectedEdge = this.edgeEntries[selectedIndex];
      if (selectedEdge) {
        this.loadEdge(selectedEdge, selectedIndex);
      } else {
        this.loadMalformedEdge(selectedIndex);
      }
    } else {
      this.selectedEdgeId.set(null);
      this.selectedEdgeIndex.set(null);
      this.selectedEdgeBaseline = null;
      this.isAddingEdge.set(false);
      this.edgeFormValues = this.emptyFormValues();
      this.edgeFormConfig = this.buildFormConfig(false);
      this.setPendingChanges(false);
      this.errorMessage.set(null);
    }
    this.statusMessage.set(null);
  }

  private clearEditor(): void {
    this.selectedEdgeId.set(null);
    this.selectedEdgeIndex.set(null);
    this.selectedEdgeBaseline = null;
    this.selectedEdgeSource = null;
    this.isAddingEdge.set(false);
    this.edgeFormValues = this.emptyFormValues();
    this.edgeFormConfig = this.buildFormConfig(false);
    this.setPendingChanges(false);
    this.statusMessage.set(null);
    this.errorMessage.set(null);
    this.nodeOptions.set([]);
  }

  private loadEdge(edge: WorkflowDefinitionEdge, index: number): void {
    this.isAddingEdge.set(false);
    this.selectedEdgeId.set(edge.id);
    this.selectedEdgeIndex.set(index);
    this.edgeFormValues = { ...edge };
    this.selectedEdgeBaseline = JSON.stringify(edge);
    this.selectedEdgeSource = structuredClone(edge);
    this.updateNodeOptions();
    this.edgeFormConfig = this.buildFormConfig(false);
    this.errorMessage.set(null);
    this.setPendingChanges(false);
  }

  private loadMalformedEdge(index: number): void {
    this.isAddingEdge.set(false);
    this.selectedEdgeId.set(null);
    this.selectedEdgeIndex.set(index);
    this.selectedEdgeBaseline = null;
    this.selectedEdgeSource = null;
    this.edgeFormValues = this.emptyFormValues();
    this.edgeFormConfig = this.buildFormConfig(false);
    this.errorMessage.set(null);
    this.setPendingChanges(false);
  }

  private startAddEdge(
    values: WorkflowEdgeFormValues,
    statusMessage: string,
  ): void {
    this.isAddingEdge.set(true);
    this.selectedEdgeId.set(null);
    this.selectedEdgeIndex.set(null);
    this.selectedEdgeBaseline = null;
    this.selectedEdgeSource = null;
    this.edgeFormValues = values;
    this.updateNodeOptions();
    this.edgeFormConfig = this.buildFormConfig(true);
    this.setPendingChanges(false);
    this.errorMessage.set(null);
    this.statusMessage.set(statusMessage);
    this.focusAfterRender(() => this.edgeEditorHeading);
  }

  private updateNodeOptions(): void {
    const nodes = [...(this.document?.nodes ?? [])]
      .filter((node) => !!node)
      .sort(
        (left, right) =>
          left.displayOrder - right.displayOrder ||
          left.id.localeCompare(right.id),
      );
    this.nodeOptions.set(
      nodes.map((node) => ({
        value: node.id,
        viewValue: this.formatNodeOption(node, nodes),
      })),
    );
    this.sourceOptions.set(
      this.endpointOptions(nodes, 'source', this.edgeFormValues.fromNodeId),
    );
    this.destinationOptions.set(
      this.endpointOptions(nodes, 'destination', this.edgeFormValues.toNodeId),
    );
  }

  private endpointOptions(
    nodes: WorkflowDefinitionNode[],
    endpoint: 'source' | 'destination',
    savedId: string,
  ): InputSelectOption[] {
    const options = nodes
      .filter((node) =>
        endpoint === 'source'
          ? node.typeCode !== 'terminal'
          : node.typeCode !== 'entry',
      )
      .map((node) => ({
        value: node.id,
        viewValue: this.formatNodeOption(node, nodes),
      }));
    if (savedId && !options.some((option) => option.value === savedId)) {
      const saved = nodes.find((node) => node.id === savedId);
      options.push({
        value: savedId,
        viewValue: saved
          ? `${this.nodeDisplayName(saved)} (saved ${endpoint}; choose another stage)`
          : `Unavailable saved ${endpoint} — choose another stage`,
      });
    }
    return options;
  }

  private formatNodeOption(
    node: WorkflowDefinitionNode,
    nodes: WorkflowDefinitionNode[],
  ): string {
    const displayName = this.nodeDisplayName(node);
    const duplicateName = nodes.some(
      (candidate) =>
        candidate.id !== node.id &&
        this.nodeDisplayName(candidate) === displayName,
    );
    return duplicateName ? `${displayName} (${node.id})` : displayName;
  }

  private nodeDisplayName(node: WorkflowDefinitionNode): string {
    return (
      node.displayName?.trim() ||
      node.stepCode?.trim() ||
      (node.typeCode === 'entry'
        ? 'Start process'
        : node.typeCode === 'terminal'
          ? 'Finish process'
          : node.typeCode === 'routing_decision'
            ? 'Condition'
            : 'Unnamed step')
    );
  }

  private buildFormConfig(_isAdding: boolean): InputConfig[] {
    const workStageFields: InputConfig[] = [
      {
        name: 'fromNodeId',
        type: EzFormInputType.SELECT,
        label: 'From work stage',
        row: 1,
        width: '1x',
        options: this.sourceOptions,
        validation: [Validators.required],
      },
      {
        name: 'toNodeId',
        type: EzFormInputType.SELECT,
        label: 'Next work stage',
        row: 1,
        width: '1x',
        options: this.destinationOptions,
        validation: [Validators.required],
      },
    ];

    if (
      this.isRoutingSource &&
      [
        '1.3',
        '1.4',
        '1.5',
        '1.6',
        '1.7',
        '1.8',
        '1.9',
        '1.10',
        '1.11',
        '1.12',
        '1.13',
        '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(this.document?.payloadSchemaVersion ?? '')
    ) {
      workStageFields.push(
        {
          name: 'outcomeCode',
          type: EzFormInputType.SELECT,
          label: 'Routing result',
          row: 2,
          width: '1x',
          options: this.resultOptions(),
          validation: [Validators.required],
        },
        {
          name: 'pathKindCode',
          type: EzFormInputType.SELECT,
          label: 'Path behavior',
          row: 2,
          width: '1x',
          options: [
            { value: '', viewValue: 'Continue' },
            { value: 'return', viewValue: 'Return for correction' },
            ...(this.edgeFormValues.pathKindCode &&
            this.edgeFormValues.pathKindCode !== 'return'
              ? [
                  {
                    value: this.edgeFormValues.pathKindCode,
                    viewValue: `Saved behavior: ${this.edgeFormValues.pathKindCode} (unavailable)`,
                  },
                ]
              : []),
          ],
        },
      );
    }
    return workStageFields;
  }

  private resultOptions(): InputSelectOption[] {
    const source = this.document?.nodes.find(
      (node) => node?.id === this.edgeFormValues.fromNodeId,
    );
    const review =
      source?.routing?.conditionCode === 'review.result' &&
      source.routing.conditionVersion === 1;
    const options: InputSelectOption[] = review
      ? [
          { value: 'approved', viewValue: 'Approved' },
          { value: 'changes_required', viewValue: 'Changes required' },
        ]
      : [];
    if (!review)
      for (const result of this.declaredResults(source?.id))
        if (
          result.code &&
          !options.some((option) => option.value === result.code)
        )
          options.push({
            value: result.code,
            viewValue: result.label || result.code,
          });
    const savedResults = [
      ...(this.document?.edges ?? [])
        .filter((edge) => edge?.fromNodeId === source?.id)
        .map((edge) => edge.outcomeCode),
      this.edgeFormValues.outcomeCode,
    ];
    for (const result of savedResults)
      if (result && !options.some((option) => option.value === result))
        options.push({
          value: result,
          viewValue: `Saved result: ${result}${review ? ' (unavailable)' : ''}`,
        });
    return options;
  }

  private declaredResults(
    sourceId: string | undefined,
  ): Array<{ code: string; label: string }> {
    if (!sourceId) return [];
    const owners = (this.document?.edges ?? [])
      .filter((edge) => edge?.toNodeId === sourceId)
      .map((edge) => edge.fromNodeId);
    return (this.document?.nodes ?? [])
      .filter((node) => node && owners.includes(node.id))
      .flatMap((node) => node.declaration?.behavior?.commands ?? [])
      .flatMap((command) => command?.outcomes ?? [])
      .filter((outcome) => !!outcome);
  }

  private get edgeEntries(): Array<WorkflowDefinitionEdge | null> {
    return (this.document?.edges ?? []) as Array<WorkflowDefinitionEdge | null>;
  }

  private emptyFormValues(): WorkflowEdgeFormValues {
    return {
      id: '',
      fromNodeId: '',
      toNodeId: '',
    };
  }

  private setPendingChanges(value: boolean): void {
    if (this.pendingChanges() === value) {
      return;
    }

    this.pendingContext = value ? this.pathContext(this.document) : null;
    this.pendingChanges.set(value);
    this.pendingChangesChange.emit(value);
  }

  private ensureFormValid(): boolean {
    const formGroup = this.edgeForm?.theFormGroup as FormGroup | undefined;
    if (!formGroup) {
      return false;
    }
    if (formGroup.valid || (formGroup.disabled && this.pendingPathState(this.document) === 'valid')) {
      return true;
    }

    formGroup.markAllAsTouched();
    this.showError('Correct the highlighted path fields before continuing.');
    return false;
  }

  private showError(message: string): void {
    this.errorMessage.set(message);
    this.statusMessage.set(null);
    this.focusAfterRender(() => this.edgeErrorSummary);
  }

  private confirmLocally(request: Parameters<WorkflowDraftConfirmationService['confirm']>[0]): Promise<boolean> {
    if (this.destroyRef.destroyed || this.localConfirmationInProgress || this.ownerViews.focusSuspended()) return Promise.resolve(false);
    this.localConfirmationInProgress = true;
    const resumeFocus = this.ownerViews.suspendFocus();
    const host = this.host.nativeElement, wasInert = host.inert;
    host.inert = true;
    const release = () => {
      this.localConfirmationInProgress = false;
      host.inert = wasInert;
      resumeFocus();
    };
    try {
      const result = this.confirmation.confirm(request);
      void result.then(release, release);
      return result;
    } catch (error) { release(); throw error; }
  }

  private focusAfterRender(
    target: () => ElementRef<HTMLElement> | undefined,
  ): void {
    setTimeout(() => {
      const element = target()?.nativeElement;
      if (this.destroyRef.destroyed || this.ownerViews.focusSuspended() || element?.closest('[hidden],[inert]')) return;
      element?.focus();
    });
  }
}
